import * as React from "react";
import { useState, useRef, useMemo } from "react";

// Third Party
import { useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";

// helpers
import { useAccessPermission } from "@/helpers/acessPermission";

import {
  createFileRoute,
  useNavigate,
  type NavigateOptions,
} from "@tanstack/react-router";
import dayjs from "dayjs";
import { useDebouncedValue } from "@mantine/hooks";

// UI
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { X, Copy } from "lucide-react";
import TableSkeleton from "@/components/skeleton/TableSkeleton";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import Pagination from "@/components/Pagination";
import {
  Select,
  SelectItem,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Services
import { getNonFinance, postFormLink } from "@/services/non-financial";

interface CoreSector {
  id: string;
  coreSector: string;
}

interface NonFinancialForm {
  id: string;
  name: string;
  coreSectorId: string;
  startDate: string;
  expiryDate: string;
  yearly: number;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
  coreSector: CoreSector;
  isAnswered: boolean;
}

interface ErrorResponse {
  message: string;
}

interface NonFinanceResponse {
  status: boolean;
  message: string;
  total: number;
  data: NonFinancialForm[];
}
interface EditProps {
  state: boolean;
  id?: string;
}
interface EditInfoProps {
  onClose?: () => void;
  open: boolean;
}
import type { FC } from "react";
import NotPermission from "@/components/NotPermission";
import { clearLocalStorage } from "@/helpers/accessLocalStorage";
export const Route = createFileRoute(
  "/_layout-private/report/non-financial-assessment/"
)({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      page: Number(search.page) || 1,
      search: (search.search as string) || "",
      createdAt: (search.createdAt as string) || "",
      startYear: Number(search.startYear) || 0,
      endYear: Number(search.endYear) || 0,
    };
  },
});

function RouteComponent() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem("companyId") || "null";
  const { nonFinancialReport } = useAccessPermission();
  const {
    page,
    search: searchParam,
    startYear: startYearParam,
    endYear: endYearParam,
  } = Route.useSearch();
  const initialZero = 0;
  const currentYear = new Date().getFullYear();

  const [filter, setFilter] = useState({
    search: searchParam,
    skip: (page - 1) * 40,
    limit: 40,
    createdAt: undefined,
    startYear: startYearParam || initialZero,
    endYear: endYearParam || initialZero,
  });

  const yearOptions = useMemo(() => {
    return [...Array(currentYear - 1975 + 1)].map((_, index) =>
      (currentYear - index).toString()
    );
  }, [currentYear]);

  const endYearOptions = useMemo(() => {
    if (!filter.startYear || filter.startYear === initialZero) {
      return yearOptions;
    }
    const startYearIndex = yearOptions.findIndex(
      (year) => Number.parseInt(year) === filter.startYear
    );
    if (startYearIndex === -1) return yearOptions;
    return yearOptions.slice(0, startYearIndex + 1);
  }, [filter.startYear, yearOptions]);

  const [debounceSearch] = useDebouncedValue(filter.search, 500);

  const handleStartYearChange = (value: string) => {
    const yearValue = Number(value);
    setFilter((prev) => ({ ...prev, startYear: yearValue }));

    if (filter.endYear !== initialZero && filter.endYear < yearValue) {
      setFilter((prev) => ({ ...prev, endYear: yearValue }));
      navigate({
        search: (prev) => ({
          ...prev,
          startYear: yearValue,
          endYear: yearValue,
          page: 1,
        }),
      } as NavigateOptions);
    } else {
      navigate({
        search: (prev) => ({
          ...prev,
          startYear: yearValue,
          page: 1,
        }),
      } as NavigateOptions);
    }
  };

  const handleEndYearChange = (value: string) => {
    const yearValue = Number(value);
    setFilter((prev) => ({ ...prev, endYear: yearValue }));
    navigate({
      search: (prev) => ({
        ...prev,
        endYear: yearValue,
        page: 1,
      }),
    } as NavigateOptions);
  };

  const {
    data: NonFinanceData,
    error,
    isLoading,
  } = useQuery<NonFinanceResponse, AxiosError<ErrorResponse>>({
    queryKey: [
      "boards",
      companyId,
      page,
      debounceSearch,
      filter.startYear,
      filter.endYear,
    ],
    queryFn: () => {
      const params: { [key: string]: string | number } = {
        skip: (page - 1) * 40,
        limit: 40,
      };

      if (debounceSearch.trim()) {
        params.search = debounceSearch;
      }

      if (filter.startYear !== initialZero) {
        params.startYear = filter.startYear;
      }

      if (filter.endYear !== initialZero) {
        params.endYear = filter.endYear;
      }

      return getNonFinance(params);
    },
  });

  if (error) {
    if (error?.response?.data?.message === "LOGGED_OUT") {
      console.log("clearing storage due to logged out");
      clearLocalStorage();
      window.location.reload();
    }
  }

  // Functions
  const handlePageClick = (page: number) => {
    navigate({
      search: (prev) => ({
        ...prev,
        page,
      }),
    } as NavigateOptions);
  };

  const [show, setShow] = React.useState(false);
  const toshow = () => {
    setShow(true);
  };

  const Edit: FC<EditProps> = ({ state, id }) => {
    if (state !== true) {
      return (
        <div className="flex gap-x-2 items-center">
          <Button
            onClick={() => {
              try {
                if (!id) return;
                localStorage.setItem("Question", id!);
                toshow();
              } catch (error) {
                console.error("Error setting Question ID:", error);
              }
            }}
            variant="secondary"
            className="flex items-center text-blue-500 rounded-md border border-gray-300"
          >
            ສ້າງລິ້ງ
          </Button>
          <Button
            onClick={() => {
              try {
                if (!id) return;
                localStorage.setItem("Question", id!);
                navigate({
                  to: "/report/non-financial-assessment/answer",
                });
              } catch (error) {
                console.error("Error saving question ID:", error);
              }
            }}
            variant="secondary"
            className="flex items-center text-blue-500 rounded-md border border-gray-300"
          >
            ຕອບ
          </Button>
        </div>
      );
    }
  };
  const ToCheck: FC<EditProps> = ({ state }) => {
    if (state === true) {
      return <p className="w-full text-green-500">ຕອບແລ້ວ</p>;
    }
    return <p className="text-red-500">ຍັງບໍ່ຕອບ</p>;
  };

  return nonFinancialReport.isRead ? (
    <div className="flex flex-col w-full">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbPage>ການປະເມີນ</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <Card className="mt-8 w-full">
        <CardHeader>
          <CardTitle>ການປະເມີນທີ່ບໍ່ແມ່ນການເງິນ</CardTitle>
          <CardDescription>ລາຍການການປະເມີນ </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-between items-end mt-5 mb-5 w-full">
            <div className="flex gap-x-2 justify-start w-10/12">
              <div>
                <Label htmlFor="">ປີເລີ່ມຕົ້ນ</Label>
                <Select
                  value={filter.startYear.toString()}
                  onValueChange={handleStartYearChange}
                >
                  <SelectTrigger className="w-[150px] indent-1">
                    <SelectValue placeholder="ເລືອກປີ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">ທັງໝົດ</SelectItem>
                    {yearOptions.map((year) => (
                      <SelectItem key={year} value={year}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="">ປີສິ້ນສຸດ</Label>
                <Select
                  value={filter.endYear.toString()}
                  onValueChange={handleEndYearChange}
                  disabled={
                    !filter.startYear || filter.startYear === initialZero
                  }
                >
                  <SelectTrigger className="w-[150px] indent-1">
                    <SelectValue placeholder="ເລືອກປີ" />
                  </SelectTrigger>
                  <SelectContent>
                    {endYearOptions.map((year) => (
                      <SelectItem key={year} value={year}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <Table className="border">
            <TableHeader>
              <TableRow className="border">
                {tableHeaders.map((header) => (
                  <TableHead key={header.label} className={"whitespace-nowrap"}>
                    {header.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            {isLoading ? (
              <TableSkeleton />
            ) : (
              <TableBody>
                {NonFinanceData?.data?.map((invoice, index) => (
                  <TableRow
                    key={invoice.id}
                    className=" cursor-pointer [&>*]:whitespace-nowrap"
                  >
                    <TableCell className="font-medium text-center">
                      {index + 1}
                    </TableCell>

                    <TableCell>{invoice?.name}</TableCell>
                    {/* <TableCell>{invoice?.coreSector?.coreSector}</TableCell> */}
                    <TableCell>{invoice?.yearly}</TableCell>
                    <TableCell>
                      {invoice.startDate
                        ? dayjs(invoice?.startDate).format("DD/MM/YYYY")
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {invoice.expiryDate
                        ? dayjs(invoice?.expiryDate).format("DD/MM/YYYY")
                        : "-"}
                    </TableCell>

                    <TableCell className="whitespace-nowrap">
                      {nonFinancialReport.isWrite && (
                        <ToCheck state={invoice?.isAnswered} id={invoice?.id} />
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {nonFinancialReport.isUpdate && (
                        <Edit state={invoice?.isAnswered} id={invoice?.id} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>

          <Pagination
            handlePageClick={handlePageClick}
            totalItems={NonFinanceData?.total || 0}
            itemsPerPage={40}
            currentPage={page}
          />
        </CardContent>
        {show ? (
          <ShowInfomation open={show} onClose={() => setShow(false)} />
        ) : null}
      </Card>
    </div>
  ) : (
    <NotPermission />
  );
}

const tableHeaders = [
  {
    label: "ລຳດັບ",
  },
  { label: "ຊື່ແບບຟອມ" },

  // { label: "ຂະແໜ່ງ" },
  {
    label: "ປະຈຳປີ",
  },
  { label: "ວັນທີເລີ່ມ" },
  { label: "ວັນທີສິ້ນສຸດ" },
  { label: "ສະຖານະ" },
  { label: "ຈັດການ" },
];

interface CopyFieldProps {
  value: string;
  label: string;
  loading?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  onCopy: () => void;
}

const CopyField: FC<CopyFieldProps> = ({
  value,
  label,
  loading,
  inputRef,
  onCopy,
}) => (
  <div className="flex flex-col gap-y-4 w-10/12">
    <p className="font-bold">
      {label} <span className="text-red-500">*</span>
    </p>
    <div className="flex justify-between items-center w-full">
      {loading ? (
        inputRef ? (
          <Input
            ref={inputRef}
            className="max-w-md text-lg text-blue-500 underline truncate border-none cursor-pointer focus:outline-none focus:border-none"
            value="Loading..."
            readOnly
          />
        ) : (
          <p>loading...</p>
        )
      ) : inputRef ? (
        <Input
          ref={inputRef}
          className="max-w-md text-lg text-blue-500 underline truncate border-none cursor-pointer focus:outline-none focus:border-none"
          value={value}
          readOnly
        />
      ) : (
        <p>{value}</p>
      )}
      <button
        type="button"
        onClick={onCopy}
        className="flex gap-x-2 border-none"
      >
        ກັອບປີ້ລິ້ງ <Copy />
      </button>
    </div>
  </div>
);

const ShowInfomation: FC<EditInfoProps> = ({ onClose, open }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const formId = localStorage.getItem("Question");
  const { toast } = useToast();

  const { data: listdata, isLoading } = useQuery({
    queryKey: ["linkses", formId],
    queryFn: () => postFormLink({ formId: formId! }),
    enabled: !!formId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const copyToClipboard = async (text: string, title: string) => {
    try {
      // Check if navigator.clipboard is available (modern browsers with HTTPS)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast({
          title: `Copied ${title}`,
          description: text,
        });
        return;
      }

      // Fallback method for older browsers or non-HTTPS
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);

      if (successful) {
        toast({
          title: `Copied ${title}`,
          description: text,
        });
      } else {
        throw new Error("Copy command failed");
      }
    } catch (err) {
      console.error("Failed to copy: ", err);

      // Final fallback - show a modal with the text to copy manually
      const fallbackCopy = () => {
        const modal = document.createElement("div");
        modal.style.cssText = `
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: white;
          border: 2px solid #ccc;
          border-radius: 8px;
          padding: 20px;
          z-index: 10000;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          max-width: 400px;
          word-wrap: break-word;
        `;

        modal.innerHTML = `
          <h3 style="margin: 0 0 10px 0; color: #333;">ກະລຸນາກັອບປີ້ ${title} ດ້ວຍມື:</h3>
          <textarea readonly style="width: 100%; height: 100px; margin: 10px 0; padding: 8px; border: 1px solid #ccc; border-radius: 4px;">${text}</textarea>
          <button onclick="this.parentElement.remove()" style="background: #007bff; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;">ປິດ</button>
        `;

        document.body.appendChild(modal);

        // Auto-select the text in textarea
        const textarea = modal.querySelector("textarea") as HTMLTextAreaElement;
        textarea.select();

        // Remove after 10 seconds
        setTimeout(() => {
          if (document.body.contains(modal)) {
            document.body.removeChild(modal);
          }
        }, 10000);
      };

      fallbackCopy();

      toast({
        title: "Copy manually",
        description: "Please copy the text from the popup window",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="fixed max-w-3xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          <X />
        </button>

        <DialogHeader className="flex justify-between items-center mt-4 bg-gray-50">
          <DialogTitle className="text-lg font-semibold text-center">
            ການສ້າງລິ້ງ ແລະ ລະຫັດຜ່ານ
          </DialogTitle>
          <DialogDescription>ກະລຸນາກັອບປີ້ ແລະ ລະຫັດຜ່ານ</DialogDescription>
        </DialogHeader>

        <form className="flex flex-col items-center space-y-4 w-full h-60">
          <CopyField
            label="ລິ້ງ"
            value={listdata?.data?.link || ""}
            loading={isLoading}
            inputRef={inputRef}
            onCopy={() =>
              inputRef.current &&
              copyToClipboard(inputRef.current.value, "link")
            }
          />
          <CopyField
            label="ລະຫັດຜ່ານ"
            value={listdata?.data?.password || ""}
            loading={isLoading}
            onCopy={() =>
              listdata?.data?.password &&
              copyToClipboard(listdata.data.password, "password")
            }
          />
        </form>

        <div className="flex w-full">
          <Button onClick={onClose} className="w-full">
            ສຳເລັດແລ້ວ
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
